import { type MutableRefObject, type PropsWithChildren, useState } from "react";
import type { FiatPrice } from "@b2bportal/air-booking-api";

import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { getDarkModePreferred } from "@hopper-b2b/themes";
import {
  ActionButton,
  ButtonWrap,
  IconComponent,
  IconName,
  MobilePopoverCard,
} from "@hopper-b2b/ui";
import { uberOpenExternalLink, useEnableDarkMode } from "@hopper-b2b/utilities";
import clsx from "clsx";
import dayjs from "dayjs";

import AirplaneBlackIcon from "../../assets/client/airplane-depart-chfar.svg?react";
import AirplaneWhiteIcon from "../../assets/client/darkMode/airplane-depart-chfar-white.svg?react";
import InfoWhiteIcon from "../../assets/client/darkMode/info-white.svg?react";
import MoneyWhiteIcon from "../../assets/client/darkMode/money-note-white.svg?react";
import TagWhiteIcon from "../../assets/client/darkMode/tag-white.svg?react";
import FtuHeaderImage from "../../assets/client/ftu-header.svg";
import InfoBlackIcon from "../../assets/client/info.svg?react";
import MoneyBlackIcon from "../../assets/client/money-note.svg?react";
import TagBlackIcon from "../../assets/client/tag.svg?react";
import {
  mapFtuVariantToCashbackCalculator,
  mapFtuVariantToCopy,
  mapFtuVariantToExpiry,
  useFtuExperiment,
} from "../../utils/ftuExperiments";
import styles from "./styles.module.scss";

const FtuBannerWrapper = ({
  children,
  className,
  ref,
}: PropsWithChildren<{
  className?: string;
  ref?: MutableRefObject<HTMLDivElement>;
}>) => {
  const { enabled } = useFtuExperiment();
  const darkModeAllowed = useEnableDarkMode();
  const isDarkMode = darkModeAllowed && getDarkModePreferred();
  if (!enabled) return null;

  const TagIcon = isDarkMode ? TagWhiteIcon : TagBlackIcon;
  return (
    <div
      ref={ref}
      className={clsx("uber-ftu-banner", styles.bannerContainer, className)}
    >
      <TagIcon className="tag-icon" />
      {children}
      <FtuTermsAndConditions />
    </div>
  );
};

export const FtuFlightShopBanner = () => {
  const { t } = useI18nContext();
  const { enabled, variant } = useFtuExperiment();
  const copyVariant = mapFtuVariantToCopy[variant];

  if (!enabled || !copyVariant) return null;
  const title = copyVariant.flightShopBanner?.title;

  return (
    <FtuBannerWrapper>
      <p className="banner-copy">{t(title)}</p>
    </FtuBannerWrapper>
  );
};

export const FtuFlightReviewBanner = () => {
  const { t } = useI18nContext();
  const { enabled, variant } = useFtuExperiment();
  const copyVariant = mapFtuVariantToCopy[variant];

  if (!enabled || !copyVariant) return null;

  const title = copyVariant.flightReviewScreenBanner?.title;

  return (
    <FtuBannerWrapper className={styles.flightReviewBanner}>
      <p className="banner-copy">{t(title)}</p>
    </FtuBannerWrapper>
  );
};

export const FtuCashbackText = ({
  price,
  paxCount,
}: {
  price: FiatPrice;
  paxCount: number;
}) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const { enabled, variant } = useFtuExperiment();
  const copyVariant = mapFtuVariantToCopy[variant];

  if (!enabled || !copyVariant) return null;

  const copy = copyVariant.flightReviewScreenBanner?.cashBackInPriceBreakdown;
  const getCashbackValue = mapFtuVariantToCashbackCalculator[variant];
  const cashbackValue = getCashbackValue(price, paxCount);

  return cashbackValue.value ? (
    <p className={clsx("uber-ftu-cashback", styles.ftuCashbackText)}>
      {t(copy, {
        amount: formatFiatCurrency(cashbackValue, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }),
      })}
    </p>
  ) : null;
};

export const FtuTermsAndConditions = () => {
  const { t } = useI18nContext();
  const darkModeAllowed = useEnableDarkMode();
  const isDarkMode = darkModeAllowed && getDarkModePreferred();
  const { variant } = useFtuExperiment();

  const [openInfoModal, setOpenInfoModal] = useState<boolean>(false);
  const [openTermsModal, setOpenTermsModal] = useState<boolean>(false);

  const copyVariant = mapFtuVariantToCopy[variant];
  if (!copyVariant) return null;

  const infoModalTitle = copyVariant.termsAndConditions?.infoModalTitle;
  const infoModalValuePropCopy =
    copyVariant.termsAndConditions?.infoModalValuePropCopy;
  const termsAndConditionLinkCopy = copyVariant.termsAndConditions?.linkCopy;
  const termsAndConditionLink = copyVariant.termsAndConditions?.link;
  const termsAndConditionCopy = copyVariant.termsAndConditions?.terms;
  const bookWithUberCopy = copyVariant.termsAndConditions?.bookWithUber;

  const expiryDate = mapFtuVariantToExpiry[variant];
  const ftuExpiryDate = dayjs(expiryDate).format("DD MMMM YYYY");
  const AirplaneIcon = isDarkMode ? AirplaneWhiteIcon : AirplaneBlackIcon;
  const MoneyIcon = isDarkMode ? MoneyWhiteIcon : MoneyBlackIcon;
  const InfoIcon = isDarkMode ? InfoWhiteIcon : InfoBlackIcon;

  return (
    <div className={styles.termsAndConditions}>
      <ButtonWrap onClick={() => setOpenInfoModal(true)}>
        <InfoIcon className="info-icon" />
      </ButtonWrap>
      <MobilePopoverCard
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
        paperClassName={styles.infoModalPaper}
        className={styles.infoModal}
        topRightButton={
          <IconComponent
            name={IconName.Close}
            onClick={() => setOpenInfoModal(false)}
            className="close-button"
          />
        }
      >
        <div className="image-container">
          <img
            src={FtuHeaderImage}
            alt="tag with a percent sign"
            className="ftu-header-image"
          />
        </div>
        <div className="content">
          <h1 className="info-modal-title">{t(infoModalTitle)}</h1>
          <p className="info-modal-subtitle">
            {t("ftu.infoModal.expiryAndHowTo", {
              date: ftuExpiryDate,
            })}
          </p>

          <div className="value-prop-row">
            <AirplaneIcon className="value-prop-icon" />
            <p className="value-prop-text">{t(bookWithUberCopy)}</p>
          </div>

          <div className="value-prop-row">
            <MoneyIcon className="value-prop-icon" />
            <p className="value-prop-text">{t(infoModalValuePropCopy)}</p>
          </div>

          {/* Terms link button */}
          <div className="terms-wrapper">
            <Trans
              i18nKey={termsAndConditionLinkCopy}
              components={[
                <ButtonWrap
                  onClick={() => setOpenTermsModal(true)}
                ></ButtonWrap>,
              ]}
            />
          </div>
          <ActionButton
            onClick={() => setOpenInfoModal(false)}
            message={t("gotIt")}
          />
        </div>
      </MobilePopoverCard>
      <MobilePopoverCard
        open={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        paperClassName={styles.termsAndConditionsModalPaper}
        className={styles.termsAndConditionsModal}
        topRightButton={
          <IconComponent
            name={IconName.Close}
            onClick={() => setOpenTermsModal(false)}
            className="close-button"
          />
        }
      >
        <h1 className="modal-title">{t("ftu.termsModalTitle")}</h1>
        <p className="modal-subtitle">{t("ftu.termsAndConditions")}</p>
        <p className="modal-terms-copy">
          <Trans
            i18nKey={termsAndConditionCopy}
            components={[
              <ButtonWrap
                className="terms-link"
                onClick={() => uberOpenExternalLink(termsAndConditionLink)}
              ></ButtonWrap>,
              <strong></strong>,
            ]}
          />
        </p>
      </MobilePopoverCard>
    </div>
  );
};
